import React, { useState, useEffect } from "react";
import Sidebar from "../../../components/Sidebar/Sidebar";
import { getEvents, createEvent, updateEvent, deleteEvent } from "../../../utils/api";
import Modal from "../../../components/Modal/Modal";
import "./Events.css";
import "../../../components/AdminList/AdminList.css";
import { monthKeyOf, monthLabel, monthOptions, sortByDateDesc } from "../../../utils/monthGroups";

const EVENT_MONTH_BASES = [
  { key: "published", label: "Gepubliceerd op", getDate: (e) => e.createdOn || e.createdAt },
  { key: "event", label: "Datum evenement", getDate: (e) => e.date?.day || e.date },
];

const DEFAULT_EVENT_STREAM_URL = "https://live.suricloud.com/hls/sdtv/index.m3u8";

const initialFormData = {
  imageUrl: "",
  name: "",
  title: "",
  description: "",
  date: {
    day: "",
    startTime: { hour: 0, minute: 0 },
    endTime: { hour: 0, minute: 0 }
  },
  location: {
    address: "",
    city: "",
    state: "",
    country: ""
  },
  type: "Event",
  tvurl: "",
  livestreamurl: ""
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [searchTerm, setSearchTerm] = useState("");
  const [monthBasisKey, setMonthBasisKey] = useState("published");
  const [monthFilter, setMonthFilter] = useState("");
  const [groupByMonth, setGroupByMonth] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [viewImageModal, setViewImageModal] = useState(false);
  const [viewImageUrl, setViewImageUrl] = useState("");

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const data = await getEvents();
      setEvents(data);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("location.")) {
      const field = name.split(".")[1];
      setFormData(prev => ({
        ...prev,
        location: { ...prev.location, [field]: value }
      }));
    } else if (name.startsWith("startTime.")) {
      const field = name.split(".")[1];
      setFormData(prev => ({
        ...prev,
        date: { ...prev.date, startTime: { ...prev.date.startTime, [field]: parseInt(value) || 0 } }
      }));
    } else if (name.startsWith("endTime.")) {
      const field = name.split(".")[1];
      setFormData(prev => ({
        ...prev,
        date: { ...prev.date, endTime: { ...prev.date.endTime, [field]: parseInt(value) || 0 } }
      }));
    } else if (name === "type") {
      const shouldUseDefault = value === "Online Event" || value === "Live Event";
      setFormData(prev => ({
        ...prev,
        type: value,
        tvurl: shouldUseDefault && !prev.tvurl ? DEFAULT_EVENT_STREAM_URL : prev.tvurl,
        livestreamurl: shouldUseDefault && !prev.livestreamurl ? DEFAULT_EVENT_STREAM_URL : prev.livestreamurl,
      }));
    } else if (name === "day") {
      setFormData(prev => ({
        ...prev,
        date: { ...prev.date, day: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Further compress image before converting to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Calculate new dimensions (max 500px width/height) - further compressed
          const maxSize = 500;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and compress (50% quality for further reduction)
          ctx.drawImage(img, 0, 0, width, height);
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.5); // 50% quality
          
          setFormData(prev => ({ ...prev, imageUrl: compressedBase64 }));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleViewImage = (imageUrl) => {
    setViewImageUrl(imageUrl);
    setViewImageModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const eventData = {
        ...formData,
        date: {
          day: formData.date.day,
          startTime: formData.date.startTime,
          endTime: formData.date.endTime
        }
      };

      if (eventData.type === "Online Event" || eventData.type === "Live Event") {
        if (!eventData.tvurl) eventData.tvurl = DEFAULT_EVENT_STREAM_URL;
        if (!eventData.livestreamurl) eventData.livestreamurl = DEFAULT_EVENT_STREAM_URL;
      }

      if (isEditMode && selectedEvent) {
        await updateEvent(selectedEvent._id, eventData);
        alert("Event updated successfully!");
      } else {
        await createEvent(eventData);
        alert("Event created successfully!");
      }
      setIsModalOpen(false);
      fetchEvents();
    } catch (error) {
      alert(error.message || "Failed to save event");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdd = () => {
    setFormData(initialFormData);
    setIsEditMode(false);
    setSelectedEvent(null);
    setIsModalOpen(true);
  };

  const handleEdit = (event) => {
    setSelectedEvent(event);
    const isOnlineOrLive = event.type === "Online Event" || event.type === "Live Event";
    setFormData({
      imageUrl: event.imageUrl || "",
      name: event.name || "",
      title: event.title || "",
      description: event.description || "",
      date: {
        day: event.date?.day ? new Date(event.date.day).toISOString().split('T')[0] : "",
        startTime: {
          hour: event.date?.startTime?.hour ?? 0,
          minute: event.date?.startTime?.minute ?? 0
        },
        endTime: {
          hour: event.date?.endTime?.hour ?? 0,
          minute: event.date?.endTime?.minute ?? 0
        }
      },
      location: {
        address: event.location?.address || "",
        city: event.location?.city || "",
        state: event.location?.state || "",
        country: event.location?.country || ""
      },
      type: event.type || "Event",
      tvurl: event.tvurl || (isOnlineOrLive ? DEFAULT_EVENT_STREAM_URL : ""),
      livestreamurl: event.livestreamurl || (isOnlineOrLive ? DEFAULT_EVENT_STREAM_URL : "")
    });
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      try {
        await deleteEvent(id);
        fetchEvents();
      } catch (error) {
        console.error("Error deleting event:", error);
      }
    }
  };

  const categorizeEvents = (eventList) => {
    const now = new Date();
    const upcoming = [];
    const current = [];
    const old = [];

    eventList.forEach((event) => {
      const eventDate = event.date?.day ? new Date(event.date.day) : new Date(event.date);
      if (eventDate > now) {
        upcoming.push(event);
      } else if (eventDate.toDateString() === now.toDateString()) {
        current.push(event);
      } else {
        old.push(event);
      }
    });

    return { upcoming, current, old };
  };

  const { upcoming, current, old } = categorizeEvents(events);

  const getFilteredEvents = () => {
    let filtered;
    switch (activeTab) {
      case "upcoming":
        filtered = upcoming;
        break;
      case "current":
        filtered = current;
        break;
      case "old":
        filtered = old;
        break;
      default:
        filtered = events;
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (event) =>
          (event.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          (event.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          (event.location?.city || "").toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const tabEvents = getFilteredEvents();
  const monthBasis = EVENT_MONTH_BASES.find((b) => b.key === monthBasisKey) || EVENT_MONTH_BASES[0];
  const eventMonthChoices = monthOptions(tabEvents, monthBasis.getDate);
  let filteredEvents = monthFilter
    ? tabEvents.filter((e) => monthKeyOf(monthBasis.getDate(e)) === monthFilter)
    : tabEvents;
  if (groupByMonth) filteredEvents = sortByDateDesc(filteredEvents, monthBasis.getDate);
  const eventGroups = [];
  filteredEvents.forEach((e) => {
    const key = monthKeyOf(monthBasis.getDate(e));
    const last = eventGroups[eventGroups.length - 1];
    if (last && last.key === key) last.items.push(e);
    else eventGroups.push({ key, items: [e] });
  });

  const renderEventCard = (event) => (
    <div key={event._id} className="event-card">
      {event.imageUrl && (
        <div className="event-image" onClick={() => handleViewImage(event.imageUrl)} style={{ cursor: 'pointer' }}>
          <img src={event.imageUrl} alt={event.name || event.title} />
        </div>
      )}
      <div className="event-header">
        <h3>{event.name || event.title}</h3>
        <span className="event-type">{event.type}</span>
      </div>
      <div className="event-details">
        <p><strong>Date:</strong> {event.date?.day ? new Date(event.date.day).toLocaleDateString() : "-"}</p>
        <p><strong>Time:</strong> {event.date?.startTime ? `${String(event.date.startTime.hour).padStart(2, '0')}:${String(event.date.startTime.minute).padStart(2, '0')}` : "-"} - {event.date?.endTime ? `${String(event.date.endTime.hour).padStart(2, '0')}:${String(event.date.endTime.minute).padStart(2, '0')}` : "-"}</p>
        <p><strong>Location:</strong> {[event.location?.address, event.location?.city, event.location?.state, event.location?.country].filter(Boolean).join(", ") || "-"}</p>
        {event.description && <p className="event-description">{event.description}</p>}
      </div>
      <div className="event-actions">
        <button onClick={() => handleEdit(event)} className="edit-btn">Edit</button>
        <button onClick={() => handleDelete(event._id)} className="delete-btn">Delete</button>
      </div>
    </div>
  );

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content">
        <div className="events-page">
          <div className="events-header">
            <h1>Events Management</h1>
            <button className="add-btn" onClick={handleAdd}>+ Add Event</button>
          </div>

          <div className="events-stats">
            <div className="stat-card upcoming">
              <span className="stat-count">{upcoming.length}</span>
              <span className="stat-label">Upcoming</span>
            </div>
            <div className="stat-card current">
              <span className="stat-count">{current.length}</span>
              <span className="stat-label">Current</span>
            </div>
            <div className="stat-card old">
              <span className="stat-count">{old.length}</span>
              <span className="stat-label">Past</span>
            </div>
          </div>

          <div className="events-tabs">
            <button 
              className={`tab-btn ${activeTab === "upcoming" ? "active" : ""}`}
              onClick={() => setActiveTab("upcoming")}
            >
              Upcoming ({upcoming.length})
            </button>
            <button 
              className={`tab-btn ${activeTab === "current" ? "active" : ""}`}
              onClick={() => setActiveTab("current")}
            >
              Current ({current.length})
            </button>
            <button 
              className={`tab-btn ${activeTab === "old" ? "active" : ""}`}
              onClick={() => setActiveTab("old")}
            >
              Past ({old.length})
            </button>
          </div>

          <div className="month-bar">
            <label>
              Datum
              <select value={monthBasisKey} onChange={(e) => { setMonthBasisKey(e.target.value); setMonthFilter(""); }}>
                {EVENT_MONTH_BASES.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
              </select>
            </label>
            <label>
              Maand
              <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
                <option value="">Alle maanden</option>
                {eventMonthChoices.map((m) => <option key={m.key} value={m.key}>{m.label} ({m.count})</option>)}
              </select>
            </label>
            <label className="month-bar-check">
              <input type="checkbox" checked={groupByMonth} onChange={(e) => setGroupByMonth(e.target.checked)} />
              Groeperen per maand
            </label>
          </div>

          <div className="search-bar">
            <input
              type="text"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="loading">Loading events...</div>
          ) : (
            filteredEvents.length === 0 ? (
              <div className="events-grid"><div className="no-events">No events found</div></div>
            ) : groupByMonth ? (
              eventGroups.map((group) => (
                <section key={group.key} className="events-month-group">
                  <h3 className="events-month-title">
                    {monthLabel(group.key)} <span className="month-row-count">{group.items.length}</span>
                  </h3>
                  <div className="events-grid">{group.items.map(renderEventCard)}</div>
                </section>
              ))
            ) : (
              <div className="events-grid">{filteredEvents.map(renderEventCard)}</div>
            )
          )}
        </div>
      </main>

      {/* View Image Modal */}
      <Modal
        isOpen={viewImageModal}
        onClose={() => setViewImageModal(false)}
        title="Event Image"
        size="large"
      >
        <div className="image-view-modal">
          <img src={viewImageUrl} alt="Event" style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
        </div>
      </Modal>

      {/* Create/Edit Event Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={isEditMode ? "Edit Event" : "Add New Event"}
        size="large"
      >
        <form onSubmit={handleSubmit} className="event-form">
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Event name"
                />
              </div>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="Event title"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                placeholder="Event description"
              />
            </div>
            <div className="form-group">
              <label>Event Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="file-input"
              />
              {formData.imageUrl && (
                <div className="image-preview">
                  <img src={formData.imageUrl} alt="Preview" onClick={() => handleViewImage(formData.imageUrl)} style={{ cursor: 'pointer' }} />
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, imageUrl: "" }))}>
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="form-section">
            <h3>Date & Time</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  name="day"
                  value={formData.date.day}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Type *</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Event">Event</option>
                  <option value="Online Event">Online Event</option>
                  <option value="Live Event">Live Event</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Start Time</label>
                <div className="time-inputs">
                  <select
                    name="startTime.hour"
                    value={formData.date.startTime.hour}
                    onChange={handleInputChange}
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                    ))}
                  </select>
                  <span>:</span>
                  <select
                    name="startTime.minute"
                    value={formData.date.startTime.minute}
                    onChange={handleInputChange}
                  >
                    {Array.from({ length: 60 }, (_, i) => (
                      <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>End Time</label>
                <div className="time-inputs">
                  <select
                    name="endTime.hour"
                    value={formData.date.endTime.hour}
                    onChange={handleInputChange}
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                    ))}
                  </select>
                  <span>:</span>
                  <select
                    name="endTime.minute"
                    value={formData.date.endTime.minute}
                    onChange={handleInputChange}
                  >
                    {Array.from({ length: 60 }, (_, i) => (
                      <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Location</h3>
            <div className="form-group">
              <label>Address</label>
              <input
                type="text"
                name="location.address"
                value={formData.location.address}
                onChange={handleInputChange}
                placeholder="Street address"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  name="location.city"
                  value={formData.location.city}
                  onChange={handleInputChange}
                  placeholder="City"
                />
              </div>
              <div className="form-group">
                <label>State</label>
                <input
                  type="text"
                  name="location.state"
                  value={formData.location.state}
                  onChange={handleInputChange}
                  placeholder="State"
                />
              </div>
              <div className="form-group">
                <label>Country</label>
                <input
                  type="text"
                  name="location.country"
                  value={formData.location.country}
                  onChange={handleInputChange}
                  placeholder="Country"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Streaming URLs</h3>
            <div className="form-group">
              <label>TV URL</label>
              <input
                type="url"
                name="tvurl"
                value={formData.tvurl}
                onChange={handleInputChange}
                placeholder="https://..."
              />
            </div>
            <div className="form-group">
              <label>Livestream URL</label>
              <input
                type="url"
                name="livestreamurl"
                value={formData.livestreamurl}
                onChange={handleInputChange}
                placeholder="https://..."
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? "Saving..." : (isEditMode ? "Update Event" : "Create Event")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
